import React, { useState } from 'react'
import { useTheme } from '@mui/material'
import { makeStyles } from '@mui/styles'

import {
  DEFAULT_IFRAME_HEIGHT,
  IFRAME_SCALE,
} from '../../constants/htmlPreviewParams'
import { IFrameEvent, Nil } from '../../types'

const useStyles = makeStyles(
  () => ({
    previewContainer: {
      display: 'block',
      padding: 0,
      margin: 0,
      width: 600,
      minWidth: 600,
      height: DEFAULT_IFRAME_HEIGHT,
      border: 0,
      transformOrigin: 'left top',
      '&::-webkit-scrollbar': {
        '-moz-appearance': 'none',
        '-webkit-appearance': 'none',
      },
    },
  }),
  { name: 'HtmlPreviewIFrame' },
)

export interface HtmlPreviewIframeProps {
  readonly allowPointerEvents?: boolean
  readonly css?: string
  readonly html?: string | Nil
  readonly iFrameLoaded?: (event: IFrameEvent) => void
  readonly iFrameScale?: number
  readonly transformOrigin: string
}

export const HtmlPreviewIFrame = ({
  css,
  html,
  transformOrigin,
  iFrameLoaded,
  iFrameScale = IFRAME_SCALE,
  allowPointerEvents = false,
}: HtmlPreviewIframeProps) => {
  const classes = useStyles()
  const theme = useTheme()
  const transform = `scale(${iFrameScale})`

  const defaultCss = `
    img {
      object-fit: cover;
      max-width: 100%;
    }
    ul.todo-list {
      padding-left: ${theme.spacing(2)};
      list-style-type: none;
    }
  `

  const [height, setHeight] = useState<number | string>(DEFAULT_IFRAME_HEIGHT)

  const pointerEvents = allowPointerEvents
    ? ('auto' as const)
    : ('none' as const)
  const iFrameStyles = { height, transformOrigin, transform, pointerEvents }

  const onFrameLoaded = (event: IFrameEvent) => {
    const { target } = event

    const defaultStyle = document.createElement('style')
    defaultStyle.innerHTML = defaultCss
    target?.contentDocument?.head.appendChild(defaultStyle)

    if (css) {
      const style = document.createElement('style')
      style.innerHTML = css
      target?.contentDocument?.head.appendChild(style)
    }

    setHeight(target?.contentDocument?.documentElement?.scrollHeight || 0)

    if (iFrameLoaded) {
      iFrameLoaded(event)
    }
  }

  if (!html) {
    return null
  }

  return (
    // eslint-disable-next-line react/iframe-missing-sandbox
    <iframe
      className={classes.previewContainer}
      srcDoc={html}
      style={iFrameStyles}
      title="Preview Container"
      onLoad={onFrameLoaded}
    />
  )
}
